// Left Icon Rail for Inbox v2 (PRD §1.7, WP-04).
// Folders: Mine · All · Important · Unread · Archived · Blocked · Resolved.
// Settings menu: Account filter, Follow-up list, Notifications, Groupchat, Dark mode, Add subscriber, Install App, Logout.
import { useState, useRef, useEffect } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../../lib/firebase';
import { useI18n } from '../../lib/i18n';
import { useTheme } from '../../lib/theme';
import { cn } from '../../lib/cn';
import type { ConversationFolder, LiveConversation } from '../../lib/inboxTypes';
import {
  InboxIcon,
  UserIcon,
  StarIcon,
  ArchiveIcon,
  BanIcon,
  CheckCircleIcon,
  SettingsIcon,
  ClockIcon,
  BellIcon,
  SmartphoneIcon,
  UserPlusIcon,
  SunIcon,
  MoonIcon,
  LogOutIcon,
} from '../icons';

interface InboxIconRailProps {
  currentFolder: ConversationFolder;
  onSelectFolder: (folder: ConversationFolder) => void;
  conversations: LiveConversation[];
  onOpenFollowupList: () => void;
  onOpenAddSubscriber: () => void;
  onOpenInstallApp: () => void;
  onOpenGroupchatBeta: () => void;
}

export default function InboxIconRail({
  currentFolder,
  onSelectFolder,
  conversations,
  onOpenFollowupList,
  onOpenAddSubscriber,
  onOpenInstallApp,
  onOpenGroupchatBeta,
}: InboxIconRailProps) {
  const { t } = useI18n();
  const { resolvedTheme, toggleTheme } = useTheme();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [notifSoundEnabled, setNotifSoundEnabled] = useState(() => {
    return localStorage.getItem('watag.inbox.sound') !== 'false';
  });
  const menuRef = useRef<HTMLDivElement>(null);

  // Compute folder counts
  const unreadCount = conversations.filter((c) => !c.archived && !c.blocked && c.unread > 0).length;
  const starredCount = conversations.filter((c) => !c.archived && !c.blocked && c.starred).length;
  const mineCount = conversations.filter((c) => !c.archived && !c.blocked && c.assignee === 'You').length;

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const toggleSound = () => {
    const next = !notifSoundEnabled;
    setNotifSoundEnabled(next);
    localStorage.setItem('watag.inbox.sound', String(next));
  };

  const folders: { id: ConversationFolder; label: string; icon: any; count?: number }[] = [
    { id: 'mine', label: t('inbox.folders.mine') || 'Mine', icon: UserIcon, count: mineCount },
    { id: 'all', label: t('inbox.folders.all') || 'All', icon: InboxIcon },
    { id: 'starred', label: t('inbox.folders.starred') || 'Important', icon: StarIcon, count: starredCount },
    { id: 'unread', label: t('inbox.folders.unread') || 'Unread', icon: BellIcon, count: unreadCount },
    { id: 'archived', label: t('inbox.folders.archived') || 'Archived', icon: ArchiveIcon },
    { id: 'blocked', label: t('inbox.folders.blocked') || 'Blocked', icon: BanIcon },
    { id: 'resolved', label: t('inbox.folders.resolved') || 'Resolved', icon: CheckCircleIcon },
  ];

  return (
    <div className="flex h-full w-14 sm:w-16 flex-col items-center justify-between border-e border-line bg-slate-50 py-3 dark:border-slate-800 dark:bg-slate-900/90">
      {/* Folders List */}
      <div className="flex w-full flex-col items-center gap-1.5">
        {folders.map((f) => {
          const Icon = f.icon;
          const active = currentFolder === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => onSelectFolder(f.id)}
              title={f.label}
              aria-label={f.label}
              className={cn(
                'relative flex h-10 w-10 sm:h-11 sm:w-11 flex-col items-center justify-center rounded-xl transition-colors',
                active
                  ? 'bg-brand text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-200/60 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200',
              )}
            >
              <Icon size={19} />
              {f.count !== undefined && f.count > 0 && (
                <span
                  className={cn(
                    'absolute -top-1 -end-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold shadow-sm',
                    active ? 'bg-white text-brand' : 'bg-brand text-white',
                  )}
                >
                  {f.count > 99 ? '99+' : f.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Settings Menu Gear */}
      <div ref={menuRef} className="relative">
        <button
          type="button"
          onClick={() => setSettingsOpen((v) => !v)}
          title={t('inbox.settings.title') || 'Settings'}
          aria-label={t('inbox.settings.title') || 'Settings'}
          aria-haspopup="menu"
          aria-expanded={settingsOpen}
          className={cn(
            'grid h-10 w-10 sm:h-11 sm:w-11 place-items-center rounded-xl text-slate-600 transition-colors hover:bg-slate-200/60 dark:text-slate-400 dark:hover:bg-slate-800',
            settingsOpen && 'bg-slate-200 text-slate-900 dark:bg-slate-800 dark:text-slate-200',
          )}
        >
          <SettingsIcon size={20} />
        </button>

        {settingsOpen && (
          <div
            role="menu"
            className="absolute bottom-0 start-full z-50 ms-2 w-60 overflow-hidden rounded-card border border-line bg-white py-1 shadow-2xl dark:border-slate-800 dark:bg-slate-900"
          >
            <div className="border-b border-line px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-ink-4 dark:border-slate-800 dark:text-slate-500">
              {t('inbox.settings.menuTitle') || 'Inbox Options'}
            </div>

            {/* Follow-up List */}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setSettingsOpen(false);
                onOpenFollowupList();
              }}
              className="flex w-full items-center gap-2.5 px-3.5 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <ClockIcon size={16} className="text-amber-500" />
              <span>{t('inbox.settings.followupList') || 'Follow-up List'}</span>
            </button>

            {/* Notifications Toggle */}
            <button
              type="button"
              role="menuitem"
              onClick={toggleSound}
              className="flex w-full items-center justify-between px-3.5 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <div className="flex items-center gap-2.5">
                <BellIcon size={16} className={notifSoundEnabled ? 'text-primary' : 'text-slate-400'} />
                <span>{t('inbox.settings.sound') || 'Chime Notifications'}</span>
              </div>
              <span className={cn('text-[10px] font-bold px-1.5 py-0.5 rounded', notifSoundEnabled ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400')}>
                {notifSoundEnabled ? 'ON' : 'OFF'}
              </span>
            </button>

            {/* Groupchat Settings (Beta) */}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setSettingsOpen(false);
                onOpenGroupchatBeta();
              }}
              className="flex w-full items-center gap-2.5 px-3.5 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <span className="text-sm">👥</span>
              <div className="flex items-center gap-1.5">
                <span>{t('inbox.settings.groupchat') || 'Groupchat (Beta)'}</span>
              </div>
            </button>

            {/* Dark Mode Toggle */}
            <button
              type="button"
              role="menuitem"
              onClick={toggleTheme}
              className="flex w-full items-center gap-2.5 px-3.5 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              {resolvedTheme === 'dark' ? (
                <SunIcon size={16} className="text-amber-400" />
              ) : (
                <MoonIcon size={16} className="text-indigo-500" />
              )}
              <span>
                {resolvedTheme === 'dark'
                  ? t('inbox.settings.lightMode') || 'Light Mode'
                  : t('inbox.settings.darkMode') || 'Dark Mode'}
              </span>
            </button>

            {/* Add WhatsApp Subscriber */}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setSettingsOpen(false);
                onOpenAddSubscriber();
              }}
              className="flex w-full items-center gap-2.5 px-3.5 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <UserPlusIcon size={16} className="text-primary" />
              <span>{t('inbox.settings.addSubscriber') || 'Add WhatsApp Subscriber'}</span>
            </button>

            {/* Install App (PWA) */}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setSettingsOpen(false);
                onOpenInstallApp();
              }}
              className="flex w-full items-center gap-2.5 border-t border-line px-3.5 py-2 text-start text-xs font-bold text-brand hover:bg-brand-50/50 dark:border-slate-800 dark:hover:bg-brand-950/20"
            >
              <SmartphoneIcon size={16} />
              <span>{t('inbox.settings.installApp') || 'Install App (PWA)'}</span>
            </button>

            {/* Logout */}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setSettingsOpen(false);
                void signOut(auth);
              }}
              className="flex w-full items-center gap-2.5 border-t border-line px-3.5 py-2 text-start text-xs font-medium text-rose-600 hover:bg-rose-50 dark:border-slate-800 dark:text-rose-400 dark:hover:bg-rose-950/30"
            >
              <LogOutIcon size={16} />
              <span>{t('inbox.settings.logout') || 'Log Out'}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
